// Pure helpers for the Connected apps tab: naming, search, grouping, counts.
import { formatDate } from '../../../lib/formatDate';

// Google reports an app with no registered name as its raw OAuth client id.
const RAW_CLIENT_ID = /\.apps\.googleusercontent\.com$/i;

export const isUnnamed = (text) => RAW_CLIENT_ID.test(String(text || '').trim());
export const appName = (app) => (isUnnamed(app.display_text) ? 'Unnamed app' : app.display_text);
// Destructive actions and toasts need a name that tells two unnamed apps apart:
// the last 6 characters of the client id (before Google's fixed domain suffix,
// which is identical on every id and would tell IT nothing).
export function appLabel(app) {
  if (!isUnnamed(app.display_text)) return app.display_text;
  const id = String(app.display_text).trim().replace(RAW_CLIENT_ID, '');
  return `Unnamed app (…${id.slice(-6)})`;
}
export const tileLetter = (app) => (String(appName(app) || '').trim().charAt(0) || '?').toUpperCase();

// Matches the shown name, and the raw id too so IT can paste an id they are chasing.
export function filterApps(apps, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return apps;
  return apps.filter((a) => appName(a).toLowerCase().includes(q) || String(a.display_text || '').toLowerCase().includes(q));
}

// Nexus's own sign-in sits in its own card above the lists: it can't be
// disconnected from here and IT closes it by deactivating the Nexus account, so
// it is neither a connected app to review nor an item on IT's to-close list.
// The card shows the grant this server signs people in with; any other Nexus
// environment's grant is hidden with it. The server decides `is_nexus` by OAuth
// client id (GOOGLE_CLIENT_ID + NEXUS_OTHER_CLIENT_IDS); a row is NEVER hidden
// for its name, since any third-party app can call itself "Nexus RTG".
export const nexusApp = (apps) => (apps || []).find((a) => a.is_nexus_current)
  || (apps || []).find((a) => a.is_nexus) || null;

export const visibleApps = (apps) => (apps || []).filter((a) => !a.is_nexus);

// Google hides its own device/system sign-ins from "Linked apps"; we keep them
// but out of the main list. `macOS` is deliberately NOT here: Google shows it.
const DEVICE_NAMES = ['android device', 'google chrome', 'chromecast', 'ios', 'ios account manager'];

// 'device' | 'unnamed' | 'app'. Nexus's own row is always a normal app.
export function appKind(app) {
  if (app.is_nexus) return 'app';
  if (app.native_app || DEVICE_NAMES.includes(String(app.display_text || '').trim().toLowerCase())) return 'device';
  if (isUnnamed(app.display_text)) return 'unnamed';
  return 'app';
}
export const isAppKind = (a) => appKind(a) === 'app';

const OTHER_LABEL = 'Devices and other sign-ins';

// Own view: what is still connected vs gone. IT view: a to-do list. Device and
// unnamed sign-ins live in their own collapsed group and are never "to close".
// Empty groups are dropped; the primary group is the one shown open by default.
export function groupApps(apps, { itView }) {
  const defs = itView
    ? [
      { key: 'needs-closing', label: 'Needs closing', primary: true, test: (a) => isAppKind(a) && !a.closed_at },
      { key: 'closed', label: 'Closed', primary: false, test: (a) => isAppKind(a) && !!a.closed_at },
      { key: 'other', label: OTHER_LABEL, primary: false, test: (a) => !isAppKind(a) },
    ]
    : [
      { key: 'connected', label: 'Connected', primary: true, test: (a) => isAppKind(a) && a.still_granted },
      { key: 'other', label: OTHER_LABEL, primary: false, test: (a) => !isAppKind(a) && a.still_granted },
      { key: 'disconnected', label: 'Disconnected', primary: false, test: (a) => !a.still_granted },
    ];
  return defs
    .map((d) => ({ key: d.key, label: d.label, primary: d.primary, apps: visibleApps(apps).filter(d.test) }))
    .filter((g) => g.apps.length > 0);
}

// Filter pills: totals over every row, whatever the search says.
export function pillCounts(allApps) {
  const apps = visibleApps(allApps);
  return {
    all: apps.length,
    signin: apps.filter((a) => a.signs_in).length,
    access: apps.filter((a) => a.access && a.access.length > 0).length,
  };
}

export function filterByPill(apps, pill) {
  if (pill === 'signin') return apps.filter((a) => a.signs_in);
  if (pill === 'access') return apps.filter((a) => a.access && a.access.length > 0);
  return apps;
}

// Each count phrase is glued with no-break spaces so "7 other sign-ins" never
// splits across lines; only the " · " separators may wrap.
const NB = '\u00a0';
const phrase = (n, one, many) => `${n}${NB}${(n === 1 ? one : many).replace(/ /g, NB)}`;
const others = (n) => phrase(n, 'other sign-in', 'other sign-ins');

// Leads with app-kind rows (the list people act on), then names whatever else
// the All pill counts, so the line and the pill always add up. It never says
// "signed in": "43 apps signed in" beside "Sign in with Google (48)" read as
// one claim with two numbers (the pill counts every row that signs in,
// devices and disconnected included). The IT view no longer repeats "N still
// need closing": the "x of y closed" counter and the "Needs closing (n)"
// heading already say it, and three counts read as noise.
export function summaryLine(allApps, { itView }) {
  const visible = visibleApps(allApps);
  const apps = visible.filter(isAppKind);
  if (itView) {
    const other = visible.length - apps.length;
    return `${phrase(apps.length, 'app account', 'app accounts')}${other ? ` · ${others(other)}` : ''}`;
  }
  const n = apps.filter((a) => a.still_granted).length;
  const other = visible.filter((a) => !isAppKind(a) && a.still_granted).length;
  const gone = visible.filter((a) => !a.still_granted).length;
  return phrase(n, 'connected app', 'connected apps')
    + `${other ? ` · ${others(other)}` : ''}${gone ? ` · ${phrase(gone, 'disconnected', 'disconnected')}` : ''}`;
}

// Several grants can share a name (one person had six "Robusta" rows), and a
// name alone can't tell them apart. Each repeat gets a suffix: its access date
// when that date is unique within the repeats, else an ordinal. Ordinals count
// only the undated rows, by ascending id, so they start at (1) and a grant keeps
// its number when the server's order changes (it sorts by still_granted first,
// so a disconnect used to renumber the siblings on the next load).
// Unnamed apps are skipped: appLabel already tells them apart by client id.
export function duplicateSuffixes(allApps) {
  const byName = new Map();
  visibleApps(allApps).filter((a) => !isUnnamed(a.display_text)).forEach((a) => {
    const key = appName(a).trim().toLowerCase();
    byName.set(key, [...(byName.get(key) || []), a]);
  });
  const out = {};
  byName.forEach((group) => {
    if (group.length < 2) return;
    const dateOf = (a) => (a.access_given_at ? formatDate(a.access_given_at) : '');
    const uniqueDate = (a) => {
      const d = dateOf(a);
      return d && group.filter((x) => dateOf(x) === d).length === 1 ? d : '';
    };
    group.forEach((a) => { const d = uniqueDate(a); if (d) out[a.id] = d; });
    [...group].filter((a) => !uniqueDate(a))
      .sort((x, y) => Number(x.id) - Number(y.id))
      .forEach((a, i) => { out[a.id] = String(i + 1); });
  });
  return out;
}

// "Robusta (11/08/2026)" for a repeated name, the plain name otherwise.
export const withSuffix = (text, suffix) => (suffix ? `${text} (${suffix})` : text);
